import { Button, Link } from "@fluentui/react-components";
import React, { useCallback, useMemo } from "react";
import { useEnvironment, useLogging } from "Shared/Hooks";

import { LogCategory } from "Enumerations";
import { M365PulseText } from "Shared/Components";
import { Paths } from "Shared/Utils";
import { UnauthenticatedPage } from "Components/Pages/UnauthenticatedPage";
import { applySnapshot } from "mobx-state-tree";
import { observer } from "mobx-react";
import { useRootStore } from "Store/RootStore";
import { useNavigate, useSearchParams } from "react-router";
import { useStyles } from "./LoginPage.styles";

const clearMsalBrowserState = (storage: Storage) => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key && key.startsWith("msal.")) {
            keysToRemove.push(key);
        }
    }

    keysToRemove.forEach((key) => storage.removeItem(key));
};

const LoginPageBase = () => {
    const classNames = useStyles();
    const authStore = useRootStore("AuthStore");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { trackTrace } = useLogging(LogCategory.Auth);
    const { currentEnvironment, isPublic } = useEnvironment();
    const globalSettingStore = useRootStore("GlobalSetting");

    const startLogin = useCallback(
        (loginType: "Microsoft" | "Torus" | "Gallatin") => {
            applySnapshot(globalSettingStore.auth, {});
            clearMsalBrowserState(localStorage);
            clearMsalBrowserState(sessionStorage);
            authStore.saveLoginType(loginType);
            authStore.requestInteractiveLogin();
            trackTrace("0f8fad5b-d9cb-469f-a165-70867728950e", "Login page routed to home with fresh PCA", {
                LoginType: loginType,
                RedirectStartPage: Paths.getBaseUri()
            });
            navigate("/", { replace: true });
        },
        [authStore, globalSettingStore.auth, navigate, trackTrace]
    );

    const loginMicrosoft = useCallback(() => {
        startLogin("Microsoft");
    }, [startLogin]);

    const loginTorus = useCallback(() => {
        startLogin("Torus");
    }, [startLogin]);

    const loginGallatin = useCallback(() => {
        startLogin("Gallatin");
    }, [startLogin]);

    const cleanStorage = useCallback(() => {
        applySnapshot(globalSettingStore.auth, {});
        localStorage.clear();
        sessionStorage.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const accountText = useMemo(() => {
        switch (currentEnvironment.toLowerCase()) {
            case "bleu":
                return "FRME account";
            case "delos":
                return "DEME account";
            default:
                return "Microsoft account";
        }
    }, [currentEnvironment]);

    return (
        <UnauthenticatedPage
            login
            content={
                <div className={classNames.root}>
                    <div className={classNames.login}>
                        <div className={classNames.pulseLogo}>
                            <img
                                src={Paths.getStaticFullPath("images/logo/osplogo-left.png")}
                                role="presentation"
                                width="32"
                                height="32"
                                alt="M365 Pulse logo"
                            />
                            <M365PulseText weight="semibold" className={classNames.pulseText}>
                                M365 Pulse
                            </M365PulseText>
                        </div>
                        <div className={classNames.signInButtons}>
                            <M365PulseText weight="semibold" className={classNames.signInText}>
                                Sign in with your
                            </M365PulseText>
                            <Button appearance="primary" onClick={loginMicrosoft} className={classNames.primaryLoginButton}>
                                {accountText}
                            </Button>
                            <Button appearance="outline" onClick={loginTorus} className={classNames.secondaryLoginButton}>
                                Torus account
                            </Button>
                            {isPublic && (
                                <Button appearance="outline" onClick={loginGallatin} className={classNames.secondaryLoginButton}>
                                    Gallatin account
                                </Button>
                            )}
                        </div>

                        {searchParams.has("error") && (
                            <p className={classNames.error}>
                                You have an error for signing in.{" "}
                                <span>
                                    <Link onClick={cleanStorage} as="a">
                                        Click here
                                    </Link>{" "}
                                    to clean browser caches and try to sign in again. Contact{" "}
                                    <Link href="mailto:osponcall@microsoft.com">osponcall@microsoft.com</Link> with the error ID "{searchParams.get("error")}"
                                    if the error persists.
                                </span>
                            </p>
                        )}
                        <img src={Paths.getStaticFullPath("images/logo/microsoft.png")} role="presentation" width="100" alt="Microsoft logo" />
                    </div>
                </div>
            }
        />
    );
};

export const LoginPage = observer(LoginPageBase);
